/**
 * The inspection day: the conduct sheet and its server-side draft, the results approval
 * cycle, a planned activity's conduct, and the operator's own inspection request.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Nothing here decides who may do what: the sheet carries `acts`, the acts the server
 * would accept from THIS caller now, each answered by its verb's predicate. The draft
 * lives on the server (it survives a sign-out); every save sends back the stamp the
 * sheet was loaded with, so a colleague's later save is refused rather than overwritten.
 */

import { apiJson, bool, isRecord, num, optStr, query, str } from "./client";
import { parseMeasurement, type Measurement } from "./credentials";

export type ConductMark = "PASS" | "FAIL" | "NOT_APPLICABLE";
export type ObservationKind = "POSITIVE" | "NEGATIVE";
export type ConductAct = "START" | "SAVE" | "SUBMIT" | "APPROVE" | "RETURN";

const MARKS: readonly ConductMark[] = ["PASS", "FAIL", "NOT_APPLICABLE"];
const ACTS: readonly ConductAct[] = ["START", "SAVE", "SUBMIT", "APPROVE", "RETURN"];

export interface Observation {
  kind: ObservationKind;
  text: string;
  requiresAction: boolean;
  /** The finding it was born into at approval; null while it is draft content. */
  findingId: string | null;
}

export interface ConductLine {
  id: string;
  lineNo: number;
  /** The number the checklist prints ("2.1.3"), or the line's position for an ITP line. */
  number: string;
  sectionNumber: string | null;
  sectionTitle: string | null;
  description: string;
  acceptanceCriteria: string;
  fromChecklist: boolean;
  mark: ConductMark | null;
  remarks: string | null;
  observations: Observation[];
  locked: boolean;
  /** Wave RFI ruling 1: the item a line of a several-item request belongs to. */
  itemNo: string | null;
  /** Wave CRED ruling 17: the line's measured value, its tolerance and instrument. */
  measurement: Measurement | null;
}

/**
 * Where the results go for approval (the approval chain, Mahmoud 2026-10-11), in role
 * words: before the submission where they WOULD go (`waiting` false), after it where they
 * wait (`waiting` true). `skippedRoleLabels` are links passed over because nobody there
 * may approve this record (the staffing truth). Empty `roleLabels`: nobody above.
 */
export interface ApprovalRoute {
  roleLabels: string[];
  skippedRoleLabels: string[];
  waiting: boolean;
}

function strings(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.map((one) => str(one)) : [];
}

/** The server's approval route, or null when the record has none (or the caller none). */
export function approvalRoute(raw: unknown): ApprovalRoute | null {
  if (!isRecord(raw)) {
    return null;
  }
  return {
    roleLabels: strings(raw.role_labels),
    skippedRoleLabels: strings(raw.skipped_role_labels),
    waiting: bool(raw.waiting),
  };
}

export interface ConductSheet {
  id: string;
  humanId: string;
  title: string;
  origin: string;
  workflowState: string;
  programCode: string;
  programLabel: string;
  disciplineLabel: string | null;
  contractHumanId: string;
  contractorPartyName: string;
  plannedDate: string;
  location: string | null;
  checklistCode: string | null;
  checklistTitle: string | null;
  activityId: string | null;
  activityHumanId: string | null;
  savedAt: string | null;
  savedByName: string | null;
  returnedReason: string | null;
  acts: ConductAct[];
  lines: ConductLine[];
  approvalRoute: ApprovalRoute | null;
}

function record(raw: unknown): Record<string, unknown> {
  if (!isRecord(raw)) {
    throw new Error("Expected an object body");
  }
  return raw;
}

function mark(raw: unknown): ConductMark | null {
  return MARKS.find((one) => one === raw) ?? null;
}

function observation(raw: unknown): Observation {
  const body = record(raw);
  return {
    kind: body.kind === "POSITIVE" ? "POSITIVE" : "NEGATIVE",
    text: str(body.text),
    requiresAction: bool(body.requires_action),
    findingId: optStr(body.finding_id),
  };
}

function line(raw: unknown): ConductLine {
  const body = record(raw);
  return {
    id: str(body.id),
    lineNo: num(body.line_no),
    number: str(body.number),
    sectionNumber: optStr(body.section_number),
    sectionTitle: optStr(body.section_title),
    description: str(body.description),
    acceptanceCriteria: str(body.acceptance_criteria),
    fromChecklist: bool(body.from_checklist),
    mark: mark(body.mark),
    remarks: optStr(body.remarks),
    observations: Array.isArray(body.observations) ? body.observations.map(observation) : [],
    locked: bool(body.locked),
    itemNo: optStr(body.item_no),
    measurement: parseMeasurement(body.measurement),
  };
}

export function sheet(raw: unknown): ConductSheet {
  const body = record(raw);
  const acts = Array.isArray(body.acts) ? body.acts : [];
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    title: str(body.title),
    origin: str(body.origin),
    workflowState: str(body.workflow_state),
    programCode: str(body.program_code),
    programLabel: str(body.program_label),
    disciplineLabel: optStr(body.discipline_label),
    contractHumanId: str(body.contract_human_id),
    contractorPartyName: str(body.contractor_party_name),
    plannedDate: str(body.planned_date),
    location: optStr(body.location),
    checklistCode: optStr(body.checklist_code),
    checklistTitle: optStr(body.checklist_title),
    activityId: optStr(body.activity_id),
    activityHumanId: optStr(body.activity_human_id),
    savedAt: optStr(body.saved_at),
    savedByName: optStr(body.saved_by_name),
    returnedReason: optStr(body.returned_reason),
    acts: acts.flatMap((one) => ACTS.filter((act) => act === one)),
    lines: Array.isArray(body.lines) ? body.lines.map(line) : [],
    approvalRoute: approvalRoute(body.approval_route),
  };
}

/** One item's answer on the wire (AnswerIn): the full observation set for the item. */
export interface AnswerPayload {
  line_id: string;
  mark: ConductMark | null;
  remarks: string | null;
  observations: { kind: ObservationKind; text: string; requires_action: boolean }[];
}

/** A save or a submission on the wire (DraftIn). */
export interface DraftPayload {
  saved_at: string | null;
  answers: AnswerPayload[];
}

export async function getConductSheet(
  token: string,
  verificationId: string,
  signal?: AbortSignal,
): Promise<ConductSheet> {
  return apiJson(token, `/verifications/${verificationId}/conduct`, sheet, { signal });
}

/** Begin conducting (the program's checklist is loaded), or continue the draft. */
export async function startConduct(token: string, verificationId: string): Promise<ConductSheet> {
  return apiJson(token, `/verifications/${verificationId}/conduct/start`, sheet, {
    method: "POST",
  });
}

export async function saveConductDraft(
  token: string,
  verificationId: string,
  payload: DraftPayload,
): Promise<ConductSheet> {
  return apiJson(token, `/verifications/${verificationId}/conduct/draft`, sheet, {
    method: "PUT",
    body: payload,
  });
}

export async function submitConduct(
  token: string,
  verificationId: string,
  payload: DraftPayload,
): Promise<ConductSheet> {
  return apiJson(token, `/verifications/${verificationId}/conduct/submit`, sheet, {
    method: "POST",
    body: payload,
  });
}

/** Send submitted results back to the inspector, with the reason. */
export async function returnResults(
  token: string,
  verificationId: string,
  reason: string,
): Promise<ConductSheet> {
  return apiJson(token, `/verifications/${verificationId}/conduct/return`, sheet, {
    method: "POST",
    body: { reason },
  });
}

/** Conduct a planned activity: its inspection record opens with the checklist. */
export async function conductActivity(token: string, activityId: string): Promise<string> {
  return apiJson(
    token,
    `/activities/${activityId}/conduct`,
    (raw) => str(record(raw).verification_id),
    { method: "POST" },
  );
}

export interface RequestTarget {
  contractId: string;
  contractHumanId: string;
  contractorPartyName: string;
  projectName: string | null;
}

/** The main contracts THIS caller may send an inspection request on (empty: none). */
export async function listRequestTargets(
  token: string,
  signal?: AbortSignal,
): Promise<RequestTarget[]> {
  return apiJson(
    token,
    `/inspection-requests/targets${query({})}`,
    (raw) => {
      const body = record(raw);
      const items = Array.isArray(body.items) ? body.items : [];
      return items.map((entry) => {
        const one = record(entry);
        return {
          contractId: str(one.contract_id),
          contractHumanId: str(one.contract_human_id),
          contractorPartyName: str(one.contractor_party_name),
          projectName: optStr(one.project_name),
        };
      });
    },
    { signal },
  );
}

/** The operator's request on the wire (InspectionRequestIn). */
export interface InspectionRequestPayload {
  contract_id: string;
  program_code: string;
  discipline_id: string;
  scope: string;
  proposed_date: string;
  location: string | null;
  attachment_ids?: string[];
  assignee_person_id?: string | null;
  /** Wave INST ruling 1: the instance the request covers, optional on the operator's own. */
  instance_label?: string | null;
  /** A decimal greater than zero, as text; default 1. */
  quantity?: string;
  external_asset_id?: string | null;
}

export async function requestInspection(
  token: string,
  payload: InspectionRequestPayload,
): Promise<{ id: string; humanId: string }> {
  return apiJson(
    token,
    "/inspection-requests",
    (raw) => {
      const body = record(raw);
      return { id: str(body.id), humanId: str(body.human_id) };
    },
    { method: "POST", body: payload },
  );
}
